import { useMutation, useQuery } from "@tanstack/react-query";
import rg4js from "raygun4js";
import { useEffect, useMemo, useState } from "react";
import { Alert, Button, Container, Form } from "react-bootstrap";
import { useHistory, useLocation } from "react-router-dom";
import { HashLink as Link } from "react-router-hash-link";
import ReactSelect, { type MultiValue } from "react-select";
import { toast } from "sonner";
import {
  type CreateObjectionVariant,
  ObjectionType,
  type ObjectionVariant,
  Role,
  type UpdateObjectionVariant,
} from "@/briefpoint-client";
import CustomEditor from "@/components/Editor/CustomEditor";
import Loading from "@/components/Loading";
import { FormModal } from "@/components/Modals/Modal";
import PlaceholderTutorialLink from "@/components/PlaceholderTutorialLink";
import Select from "@/components/Select";
import type { VariantOption } from "@/components/Select/Select";
import SelectCreate, {
  type TagOption,
} from "@/components/Select/TagsSelectCreate";
import { useObjectionApi, useTagApi } from "@/hooks/useApi";
import { useAuth } from "@/hooks/useAuth";
import { CaliforniaId } from "@/lib/constants";
import { getObjectionsOptions } from "@/lib/query-options";
import { caseTypeDisplay, normalizeError } from "@/lib/utils";
import { isDeletable, userCanEditVariant } from "@/utils/objectionUtils";
import styles from "./AddEditObjection.module.scss";

type Option = { label: string; value: string | null };

export default function AddEditObjection() {
  const objectionApi = useObjectionApi();
  const objectionsQuery = useQuery({
    ...getObjectionsOptions(objectionApi),
  });

  const location = useLocation();
  const initialType = new URLSearchParams(location.search).get("type");
  const isCopying = new URLSearchParams(location.search).get("copy");
  const { user, defaultJurisdiction } = useAuth();
  const [type, setType] = useState<string>();
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [additionalContent, setAdditionalContent] = useState("");
  const [isDefault, setIsDefault] = useState(false);
  const [toDefault, setToDefault] = useState(false);
  const [isSharedWithFirm, setIsSharedWithFirm] = useState<
    boolean | undefined
  >();
  const [objectionName, setObjectionName] = useState("");
  const [objectionType, setObjectionType] = useState<ObjectionType>(
    ObjectionType.Standard,
  );
  const [error, setError] = useState("");
  const [showAlert, setShowAlert] = useState(true);
  const [deletingVariant, setDeletingVariant] = useState(false);
  const history = useHistory();
  const objectionVariant = location.state as ObjectionVariant;
  const [documentSubType, setDocumentSubType] = useState<number | undefined>(
    objectionVariant?.documentSubType,
  );
  const isOwner = !objectionVariant || objectionVariant.ownerId === user?.id;
  const isChangingToFirm =
    !objectionVariant?.sharedWithFirmId && isSharedWithFirm;
  const allVariants = objectionsQuery.data?.flatMap((x) => x.variants);
  const [addingTags, setAddingTags] = useState(new Map<number, string>());
  const [availableTags, setAvailableTags] = useState<
    Map<number, string> | undefined
  >();
  const tagApi = useTagApi();
  const [caseType, setCaseType] = useState(objectionVariant?.caseTypeId);

  const defaultObjectionQuery = useQuery({
    queryKey: [
      "objectionApi.objectionGetDefault",
      Number.parseInt(type ?? "", 10),
    ],
    queryFn: () =>
      objectionApi.objectionGetDefault({
        objectionId: Number.parseInt(type ?? "", 10),
      }),
    enabled: !!type,
  });

  const createVariantMutation = useMutation({
    mutationFn: ({
      objectionId,
      createObjectionVariant,
    }: {
      objectionId: number;
      createObjectionVariant: CreateObjectionVariant;
    }) =>
      objectionApi.objectionCreateVariant({
        objectionId,
        createObjectionVariant,
      }),

    onError: (error) => {
      toast.error("Failed to create variant");
      rg4js("send", {
        error: normalizeError(error),
      });
    },
    onSuccess: () => {
      history.goBack();
    },
    onSettled: () => {
      objectionsQuery.refetch();
    },
  });

  const updateVariantMutation = useMutation({
    mutationFn: ({
      objectionId,
      variantId,
      updateObjectionVariant,
    }: {
      objectionId: number;
      variantId: number;
      updateObjectionVariant: UpdateObjectionVariant;
    }) =>
      objectionApi.objectionUpdateVariant({
        objectionId,
        variantId,
        updateObjectionVariant,
      }),
    onError: (error) => {
      toast.error("Failed to update variant");
      rg4js("send", {
        error: normalizeError(error),
      });
    },
    onSuccess: () => {
      history.goBack();
    },
    onSettled: () => {
      objectionsQuery.refetch();
    },
  });

  const deleteVariantMutation = useMutation({
    mutationFn: ({
      objectionId,
      variantId,
    }: {
      objectionId: number;
      variantId: number;
    }) => objectionApi.objectionDeleteVariant({ objectionId, variantId }),
    onError: (error) => {
      toast.error("Failed to delete variant");
      rg4js("send", {
        error: normalizeError(error),
      });
    },
    onSuccess: () => {
      history.goBack();
    },
    onSettled: () => {
      objectionsQuery.refetch();
    },
  });

  let subTypes: { [key: number]: string } = {
    1: "Interrogatories",
    2: "Requests for Production",
    3: "Requests for Admission",
  };

  if (CaliforniaId === defaultJurisdiction?.id) {
    subTypes = {
      1: "Special Interrogatories",
      2: "Requests for Production",
      3: "Requests for Admission",
      5: "Form Interrogatories",
    };

    if (objectionType === ObjectionType.Response) {
      subTypes = {
        1: "Special Interrogatories",
        2: "Requests for Production",
        3: "Requests for Admission",
      };
    }
  }

  const subtypelist: VariantOption[] = Object.keys(subTypes).map((key) => ({
    label: subTypes[Number(key)],
    value: key,
    key: "",
  }));

  useEffect(() => {
    async function loadTags() {
      const tagsList = new Map<number, string>();
      const loadedTags = await tagApi.tagFindTags({});

      for (const t of loadedTags) {
        tagsList.set(t.id, t.name);
      }
      setAvailableTags(tagsList);
    }
    loadTags();
  }, [tagApi]);

  useEffect(() => {
    if (objectionVariant?.tagIds) {
      const tagIdsSet = new Set(objectionVariant.tagIds);
      const objectionVariantTags = new Map(
        Array.from(availableTags?.entries() ?? []).filter(([key, _]) =>
          tagIdsSet.has(key),
        ),
      );
      setAddingTags(objectionVariantTags);
    }
  }, [availableTags, objectionVariant, objectionVariant?.tagIds]);

  useEffect(() => {
    if (objectionVariant) {
      setType(objectionVariant.objectionId.toString());
      setTitle(objectionVariant.name);
      setContent(objectionVariant.content);
      if (objectionVariant.additionalContent) {
        setAdditionalContent(objectionVariant.additionalContent);
      }
      if (!isCopying) {
        setIsDefault(objectionVariant.isDefault);
        setIsSharedWithFirm(!!objectionVariant.sharedWithFirmId);
        setToDefault(objectionVariant.isDefault);
      }
    } else if (initialType) {
      setType(initialType);
    }
  }, [initialType, objectionVariant, isCopying]);

  useEffect(() => {
    if (objectionsQuery.data && objectionVariant) {
      const objection = objectionsQuery.data.find(
        (objection) => objection.id === objectionVariant.objectionId,
      );
      if (objection) {
        setObjectionName(objection.name);
        setObjectionType(objection.objectionType);
      }
    }
  }, [objectionVariant, objectionsQuery.data]);

  useEffect(() => {
    if (objectionsQuery.data && type) {
      const objection = objectionsQuery.data.find(
        (objection) => objection.id === Number.parseInt(type, 10),
      );
      if (objection) {
        setObjectionName(objection.name);
        setObjectionType(objection.objectionType);
        if (initialType) {
          setContent(
            objection.variants?.find((v) => v.isDefault)?.content ?? "",
          );
        }
      }
    }
  }, [objectionsQuery.data, type, initialType]);

  const isResponseish =
    objectionType === ObjectionType.Response ||
    objectionType === ObjectionType.NoResponse;

  function handleChangeContent(val: string) {
    setContent(val);
  }

  function handleChangeAdditionalContent(value: string) {
    setAdditionalContent(value);
  }

  function handleChangeTitle(event: React.ChangeEvent<HTMLInputElement>) {
    setTitle(event.target.value);
  }

  const transformedOptions = useMemo(() => {
    const opts: Option[] = [];

    opts.push({ value: null, label: "All" });
    for (const c of defaultJurisdiction?.caseClasses ?? []) {
      opts.push({ value: c.id, label: c.name ?? "" });
      for (const s of c.subTypes ?? []) {
        opts.push({ value: s.id, label: `└${s.shortName}` });
      }
    }

    return opts;
  }, [defaultJurisdiction]);

  async function onSave() {
    let hasError = false;

    if (!title.trim()) {
      setError("Please provide a Title.");
      hasError = true;
    }

    if (!content.trim() && !additionalContent.trim()) {
      setError("Please provide some Content.");
      hasError = true;
    }

    if (!type) {
      setError("Please select an Objection Type.");
      hasError = true;
    }
    if (!hasError && type) {
      //add new tags if needed
      const newTags = Array.from(addingTags.entries()).filter(
        (tg) => tg[0] < 0,
      );
      const addedTags: Map<number, string> = new Map<number, string>();
      await Promise.all(
        newTags.map(async (nTag) => {
          const newTag = await tagApi.tagCreateTag({
            tagCreateRequest: { tagName: nTag[1] },
          });
          addedTags.set(newTag.id, newTag.name);
        }),
      );

      const tagIdsToSet = [
        ...Array.from(addedTags.keys()),
        ...Array.from(addingTags.keys()).filter((tg) => tg >= 0),
      ];

      if (
        objectionVariant === undefined ||
        isCopying ||
        (isSharedWithFirm && !user?.role?.includes(Role.FirmAdmin))
      ) {
        //Add
        createVariantMutation.mutate({
          objectionId: Number.parseInt(type, 10),
          createObjectionVariant: {
            objectionId: Number.parseInt(type, 10),
            name: title,
            content: content,
            isDefault: toDefault,
            additionalContent: additionalContent,
            sharedWithFirmId:
              isOwner && isSharedWithFirm ? user?.firmId : undefined,
            caseTypeId: caseType || null,
            tagIds: tagIdsToSet,
            documentSubType: documentSubType,
          },
        });
      } else {
        //Edit
        updateVariantMutation.mutate({
          objectionId: objectionVariant.objectionId,
          variantId: objectionVariant.id,
          updateObjectionVariant: {
            name: title,
            content: content,
            isDefault: toDefault,
            additionalContent: additionalContent,
            sharedWithFirmId:
              isOwner && isSharedWithFirm ? user?.firmId : undefined,
            caseTypeId: caseType || null,
            tagIds: tagIdsToSet,
            documentSubType: documentSubType,
          },
        });
      }
    }
  }

  async function onDelete() {
    if (objectionVariant !== null) {
      deleteVariantMutation.mutate({
        objectionId: objectionVariant.objectionId,
        variantId: objectionVariant.id,
      });
    }
  }

  async function onInsert() {
    if (!type) return;
    if (defaultObjectionQuery.data) {
      setContent(defaultObjectionQuery.data.content);
      setAdditionalContent(defaultObjectionQuery.data.additionalContent ?? "");
    }
  }

  function EditObjectionLink() {
    if (isResponseish) {
      return (
        <>
          <Link to="/library?tab=responses">Responses</Link> &gt; Edit
        </>
      );
    }
    return (
      <>
        <Link to="/library?tab=objections">All Objections</Link> &gt; Edit
      </>
    );
  }

  const options: VariantOption[] = useMemo(
    () =>
      objectionsQuery.data != null
        ? objectionsQuery.data.flatMap((v) => [
            {
              value: v.id.toString(),
              label: v.name,
              key: "",
            },
          ])
        : [],
    [objectionsQuery.data],
  );

  function handleChangeCaseType(value?: string) {
    setCaseType(value ?? "");
  }

  function handleChangeTags(value: MultiValue<TagOption>) {
    const updatedTags = new Map<number, string>();
    for (const option of value) {
      if (option.value !== null) {
        updatedTags.set(option.value, option.label);
      }
    }
    setAddingTags(updatedTags);
  }

  return (
    <>
      <Container className={`page-container ${styles.libraryContainer}`}>
        <div className={styles.breadCrumbs}>
          <Link to={"/library"}>Library</Link> {"> "}
          {objectionVariant === undefined ? "Create new" : EditObjectionLink()}
        </div>
        <h2>
          {objectionVariant === undefined ? "Add" : isCopying ? "Copy" : "Edit"}{" "}
          {isDefault ? "default" : ""}{" "}
          {isResponseish ? "response" : "objection"}
        </h2>
        <Loading isLoading={objectionsQuery.isLoading}>
          {error && (
            <Alert className="col-8" variant="danger">
              {error}
            </Alert>
          )}
          {showAlert &&
            isOwner &&
            !isChangingToFirm &&
            isSharedWithFirm &&
            !isCopying && (
              <Alert
                className="col-8"
                onClose={() => setShowAlert(false)}
                dismissible
                variant="warning"
              >
                {"You are editing a Firm Standard usable by all firm members."}
              </Alert>
            )}
          {user?.role?.includes(Role.FirmAdmin) && (
            <>
              <span className="form-label">Add to</span>
              <Form.Group>
                <Form.Check
                  id="existing-share-with-firm-no"
                  inline
                  type="radio"
                  label="Personal Library"
                  onChange={(e) => setIsSharedWithFirm(!e.target.checked)}
                  checked={!isSharedWithFirm}
                  disabled={
                    objectionVariant &&
                    !!objectionVariant.sharedWithFirmId &&
                    !isCopying
                  }
                />
                <Form.Check
                  id="existing-share-with-firm-yes"
                  type="radio"
                  inline
                  label="Firm Library"
                  onChange={(e) => setIsSharedWithFirm(e.target.checked)}
                  checked={isSharedWithFirm}
                  disabled={
                    objectionVariant &&
                    !!objectionVariant.sharedWithFirmId &&
                    !isCopying
                  }
                />
              </Form.Group>
            </>
          )}

          {objectionVariant ? (
            <h3 className="mt-2">{objectionName}</h3>
          ) : (
            <Form.Group className="mt-3">
              <Form.Label>Objection Type</Form.Label>
              <Select
                className={`select ${styles.objectionTypeSelect}`}
                options={options}
                onChange={(value) => value && setType(value)}
                selected={type}
                openPos="bottom"
              />
            </Form.Group>
          )}
          <Form.Group className={styles.titleGroup}>
            <Form.Label>Title</Form.Label>
            <Form.Control
              value={title}
              className="mb-2 font-times"
              style={{ maxWidth: 500 }}
              onChange={handleChangeTitle}
            />
          </Form.Group>
          <Form.Group className="mt-3">
            <Form.Label>Case Type</Form.Label>
            <ReactSelect
              onChange={(e) => handleChangeCaseType(e?.value ?? undefined)}
              id="type-select"
              className={`select ${styles.objectionTypeSelect}`}
              options={transformedOptions}
              value={{
                value: caseType,
                label: caseTypeDisplay(caseType, defaultJurisdiction, true),
              }}
            />
          </Form.Group>
          <Form.Group className="mt-3">
            <Form.Label>Document Type (Optional)</Form.Label>
            <Select
              className={`select ${styles.objectionTypeSelect}`}
              options={subtypelist}
              onChange={(value) => value && setDocumentSubType(Number(value))}
              selected={documentSubType?.toString()}
              openPos="bottom"
              isClearable={true}
            />
          </Form.Group>
          <div className={styles.contentWrapper}>
            <Form.Label htmlFor="content-box">
              {objectionType === ObjectionType.Response ? "Intro" : "Content"}
            </Form.Label>
            <Button
              className={styles.insertButton}
              variant="outline-primary"
              size="sm"
              onClick={onInsert}
              disabled={!type}
            >
              Insert Briefpoint Default
            </Button>
          </div>
          <CustomEditor
            handleChange={handleChangeContent}
            editorContent={content}
            showMenu
          />
          {objectionType === ObjectionType.Response && (
            <Form.Group>
              <Form.Label>Response</Form.Label>
              <CustomEditor
                handleChange={handleChangeAdditionalContent}
                editorContent={additionalContent}
                showAdvancedOptions
                showMenu
              />
            </Form.Group>
          )}
          <Form.Group className="mt-3">
            <SelectCreate
              className={`select ${styles.objectionTypeSelect}`}
              id="objection-tag"
              label="Tags"
              availableTags={availableTags}
              addingTags={addingTags}
              setAddingTags={setAddingTags}
              selected={Array.from(addingTags.keys())}
              onChange={(value) => handleChangeTags(value)}
              menuPlacement="top"
            />
          </Form.Group>
          <Form.Check
            id="existing-use-as-default"
            className="mt-2"
            type="checkbox"
            label={`Use as ${user && userCanEditVariant(objectionVariant, user) && isSharedWithFirm ? "Firm" : "Personal"} Default`}
            onChange={(e) => setToDefault(e.target.checked)}
            checked={toDefault}
          />
          <PlaceholderTutorialLink />
          <div className="d-flex mt-5">
            <Button variant="primary" onClick={onSave}>
              {!isCopying ? "Save" : "Copy and Save"}
            </Button>
            <Button variant="link" onClick={() => history.goBack()}>
              Cancel
            </Button>
            {objectionVariant &&
              user &&
              isDeletable(objectionVariant, allVariants, user) && (
                <Button
                  onClick={() => setDeletingVariant(true)}
                  className={styles.deleteButton}
                  variant="link"
                >
                  Delete
                </Button>
              )}
          </div>
        </Loading>
      </Container>
      <FormModal
        title={"Are you sure?"}
        cancelText="Cancel"
        confirmText="Yes, Delete"
        show={deletingVariant}
        onClose={() => setDeletingVariant(false)}
        onConfirm={onDelete}
      >
        <div>{`Are you sure you want to delete this ${isResponseish ? "response" : "objection"} from your library? This cannot be undone.`}</div>
      </FormModal>
    </>
  );
}
